import React, { useEffect, useState } from 'react'
import { Alert, Box, Chip, CircularProgress, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import { supabase } from '../../supabaseClient'

// The Data Science / AI Research fork — a sub-flavor within the scientific-inquiry
// track. This is the fork STRUCTURE only; mode-specific terminology and rubric
// ("context-sensitive teaching") come later. Two phases:
//   'explore' (Step 2) — a tentative choice, saved to research_question.study_mode.
//   'commit'  (Step 4) — the final commitment, locked to hypothesis_design.study_mode.
// Self-contained: loads and saves its own row so it doesn't touch the surrounding
// draft/hypothesis machinery.

type Mode = 'experimental' | 'data_science'

const OPTIONS: { value: Mode; label: string; blurb: string; icon: React.ReactNode }[] = [
  { value: 'experimental', label: 'Hands-on experiment',
    blurb: 'You change something and measure what happens.',
    icon: <ScienceOutlinedIcon fontSize="small" /> },
  { value: 'data_science', label: 'Data Science / AI',
    blurb: 'You build models on a dataset to answer the question.',
    icon: <InsightsOutlinedIcon fontSize="small" /> },
]

export default function StudyModeFork({ projectId, studentId, phase, editable = true }: {
  projectId: string; studentId: string; phase: 'explore' | 'commit'; editable?: boolean
}) {
  const [tentative, setTentative] = useState<Mode | null>(null)  // Step 2 choice
  const [committed, setCommitted] = useState<Mode | null>(null)  // Step 4 lock
  const [loaded,    setLoaded]    = useState(false)
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data: rq } = await supabase.from('research_question')
        .select('study_mode').eq('project_id', projectId).maybeSingle()
      if (cancelled) return
      setTentative(((rq as { study_mode: Mode | null } | null)?.study_mode) ?? null)
      if (phase === 'commit') {
        const { data: hd } = await supabase.from('hypothesis_design')
          .select('study_mode').eq('project_id', projectId).maybeSingle()
        if (cancelled) return
        setCommitted(((hd as { study_mode: Mode | null } | null)?.study_mode) ?? null)
      }
      setLoaded(true)
    })()
    return () => { cancelled = true }
  }, [projectId, phase])

  async function choose(mode: Mode) {
    if (!editable || saving) return
    setSaving(true); setError(null)
    if (phase === 'explore') {
      const { error: e } = await supabase.from('research_question')
        .update({ study_mode: mode }).eq('project_id', projectId)
      if (e) setError('Could not save — please try again.'); else setTentative(mode)
    } else {
      const { error: e } = await supabase.from('hypothesis_design')
        .upsert({ project_id: projectId, student_id: studentId, study_mode: mode }, { onConflict: 'project_id' })
      if (e) setError('Could not save — please try again.'); else setCommitted(mode)
    }
    setSaving(false)
  }

  if (!loaded) return null
  // Step 2 fork only appears once they've started; Step 4 always shows the commitment.
  if (phase === 'explore' && tentative == null && !editable) return null

  const current = phase === 'commit' ? (committed ?? tentative) : tentative
  const isDS    = current === 'data_science'

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, ...(isDS ? { borderColor: 'primary.main' } : {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {phase === 'commit' ? 'Confirm your project type' : 'What kind of project is this?'}
        </Typography>
        {phase === 'commit' && committed && <Chip label="Committed" size="small" color="success" />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
        {phase === 'commit'
          ? "This is your final commitment for how you'll answer your question. You can still change it here if your plan shifted."
          : "Both are scientific inquiry — just different ways to answer your question. You'll confirm this in Step 4, so it's fine to change your mind."}
      </Typography>

      <ToggleButtonGroup
        exclusive value={current} disabled={!editable || saving}
        onChange={(_, v: Mode | null) => { if (v) choose(v) }}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: '1px solid', borderColor: 'divider', borderRadius: 1.5, m: 0 } }}
      >
        {OPTIONS.map(o => (
          <ToggleButton key={o.value} value={o.value} sx={{ flex: '1 1 220px', textTransform: 'none', alignItems: 'flex-start', p: 1.5, display: 'block', textAlign: 'left' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700 }}>
              {o.icon}{o.label}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}>
              {o.blurb}
            </Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {saving && <CircularProgress size={14} sx={{ mt: 1 }} />}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      {isDS && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, fontStyle: 'italic' }}>
          Data Science project: you'll build models on a dataset to answer your question. Detailed
          data-science guidance is coming — for now, keep framing your question and variables as usual.
        </Typography>
      )}
    </Paper>
  )
}
